import {
  Add as AddIcon,
  BatchPrediction as BatchPredictionIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ExpandableTableRow from '@/components/programs/ExpandableTableRow';
import ProgramFilters from '@/components/programs/ProgramFilters';
import { DeleteConfirmDialog } from '@/components/ui/DeleteConfirmDialog';
import Notification from '@/components/ui/Notification';
import useProgramData from '@/hooks/useProgramData/forProgramsPage';
import { deleteProgram, getErrorMessage } from '@/lib/api';
import { Program } from '@/lib/api/program';

export function Programs() {
  const { t, i18n } = useTranslation(['programs', 'common']);
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPrograms, setFilteredPrograms] = useState<Program[]>([]);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<number | null>(null);

  // Notification state
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSeverity, setNotificationSeverity] = useState<'success' | 'error'>('success');

  // Use custom hook to manage program data and state
  const {
    programs,
    loading,
    error,
    selectedDate,
    supplier,
    handleDateChange,
    handleSupplierChange,
    refreshData,
  } = useProgramData();

  /**
   * Open delete dialog
   */
  const handleOpenDeleteDialog = useCallback((id: number) => {
    setProgramToDelete(id);
    setDeleteDialogOpen(true);
  }, []);

  /**
   * Close delete dialog
   */
  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setProgramToDelete(null);
  }, []);

  /**
   * Program deletion process
   */
  const handleDeleteProgram = useCallback(async () => {
    if (programToDelete === null) return;

    try {
      // Delete program using API
      const result = await deleteProgram(programToDelete);

      // Display success message
      setNotificationMessage(result.meta.message || t('programs:messages.deleteSuccess'));
      setNotificationSeverity('success');
      setNotificationOpen(true);
      refreshData(); // Refresh list
    } catch (err) {
      console.error('Failed to delete program:', err);
      // Convert error message to user-friendly format
      setNotificationMessage(getErrorMessage(err));
      setNotificationSeverity('error');
      setNotificationOpen(true);
    } finally {
      handleCloseDeleteDialog();
    }
  }, [programToDelete, refreshData, handleCloseDeleteDialog]);

  /**
   * Close notification
   */
  const handleCloseNotification = useCallback(() => {
    setNotificationOpen(false);
  }, []);

  // Search filter
  useEffect(() => {
    if (!programs) return;

    if (searchTerm) {
      const filtered = programs.filter((program) =>
        program.id.toString().includes(searchTerm.toLowerCase()) ||
        (program.supplier && program.supplier.toLowerCase().includes(searchTerm.toLowerCase())),
      );
      setFilteredPrograms(filtered);
      setPage(0); // Reset page when searching
    } else {
      setFilteredPrograms(programs);
    }
  }, [searchTerm, programs]);

  // Pagination handling
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Navigate to edit page
  const handleEdit = (id: number) => {
    navigate(`/programs/edit?id=${id}`);
  };

  // Navigate to create page
  const handleCreate = () => {
    navigate('/programs/new');
  };

  // Navigate to batch create page
  const handleBatchCreate = () => {
    navigate('/programs/batch');
  };

  // Date formatting
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Set locale based on current language
    const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('programs:title')}
        </Typography>
          <Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<BatchPredictionIcon />}
              onClick={handleBatchCreate}
              sx={{ mr: 2 }}
            >
              {t('programs:buttons.batchCreate')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              {t('programs:buttons.newProgram')}
            </Button>
          </Box>
      </Box>

      {/* Filter section */}
      <ProgramFilters
        date={selectedDate}
        supplier={supplier}
        onDateChange={handleDateChange}
        onSupplierChange={handleSupplierChange}
      />

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('programs:messages.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer>
          <Table
            sx={{
              minWidth: 650,
              '& .MuiTableCell-root': { py: 0.5 }, // Apply to all TableCells
            }}
            aria-label="program list"
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{t('programs:table.id')}</TableCell>
                <TableCell>{t('programs:table.startTime')}</TableCell>
                <TableCell>{t('programs:table.endTime')}</TableCell>
                <TableCell>{t('programs:table.duration')}</TableCell>
                <TableCell>{t('programs:table.supplier')}</TableCell>
                <TableCell align="right">{t('programs:table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
                      {t('common:messages.loading')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredPrograms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                      {t('programs:messages.noPrograms')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPrograms
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((program) => (
                    <ExpandableTableRow
                      key={program.id}
                      program={program}
                      formatDate={formatDate}
                      onEdit={handleEdit}
                      onDelete={handleOpenDeleteDialog}
                    />
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredPrograms.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('common:pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common:pagination.displayedRows', { from, to, count })}
        />
      </Paper>

      {/* Delete confirmation dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteProgram}
        title={t('programs:messages.deleteTitle')}
        message={t('programs:messages.deleteConfirm')}
      />

      {/* Notification */}
      <Notification
        open={notificationOpen}
        message={notificationMessage}
        severity={notificationSeverity}
        onClose={handleCloseNotification}
      />
    </>
  );
}

export default Programs;
